<template>
    <div
        ref="game-container"
        :class="$styleCrit.container"
    >
        <div :class="[$styleCrit.content, $styleCrit.init_state]">
            <MobileAppGameInfo
                v-if="appStore.isNativeAppEnv"
                :class="$styleCrit.short_game_info_override"
                :game="props.game"
                unavailable
            />
            <ShortGameInfo
                v-else
                :class="$styleCrit.short_game_info_override"
                :game="props.game"
                unavailable
            />

            <UnavailableGameContent
                :game="props.unavailableGameReplace"
                :unavailableGameTitle="props.game?.title"
                :class="$styleCrit.unavailable_game_override"
                @clickPlay="handlePlay"
            />

            <PlayButton
                interfaceTarget="unavailable-game-suggested-game-play"
                :label="props.game ? $tr('gameContainer.play') : ''"
                :class="$styleCrit.play_button_override"
                @click="handlePlay"
            />

            <MobileFooter
                :class="$styleCrit.mobile_footer_override"
                :game="props.game"
                :shareData
            />
        </div>
        <DesktopFooter
            :game="{ ...props.game, title: tr('unavailableGame.unavailable_game') } as APIGameType"
            :category
            :shareData
            isIconDisabled
            isGameUnavailable
        />
    </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import { useRouter } from 'vue-router'

import { useConfig } from '@/composables/config'
import { useGetExternalUrl } from '@/composables/url-helpers'
import { useTr } from '@/composables/use-tr'
import UnavailableGameContent from '@/pages/Games/components/GameContainer/UnavailableGame/UnavailableGameContent.vue'
import MobileAppGameInfo from '@/pages/Games/components/MobileAppGameInfo.vue'
import PlayButton from '@/pages/Games/components/PlayButton.vue'
import ShortGameInfo from '@/pages/Games/components/ShortGameInfo.vue'
import { getMainCategory } from '@/pages/Games/utils'
import { ROUTE_NAMES } from '@/router/constants'
import { SSR_QUERY } from '@/router/query-params'
import { useAppStore } from '@/store/app-store'
import { useLoggerStore } from '@/store/logger'
import type { APIGameType, GameCategoriesInfoType, ShortGameType } from '@/types'

import DesktopFooter from '../DesktopFooter/DesktopFooter.vue'
import MobileFooter from '../MobileFooter/MobileFooter.vue'

const props = defineProps<{
    game: APIGameType | undefined
    categories: GameCategoriesInfoType | undefined
    unavailableGameReplace?: ShortGameType
}>()

const tr = useTr()
const getExternalUrl = useGetExternalUrl()
const { VUE_APP_HOST_URL } = useConfig()
const appStore = useAppStore()
const loggerStore = useLoggerStore()
const router = useRouter()

const shareData = computed(() => ({
    title: props.game?.title,
    text: tr('gameContainer.share_game_description', { title: props.game?.title ?? '' }),
    url:
        VUE_APP_HOST_URL +
        getExternalUrl({
            name: ROUTE_NAMES.GAME_PAGE,
            params: {
                game: props.game?.hru,
            },
        }),
}))

const category = computed(() => getMainCategory(props.game, props.categories)?.title)

const handlePlay = () => {
    loggerStore.logEvent({
        event: 'custom_event',
        eventName: 'suggested_game_from_unavailable_game_click',
    })

    router.push({
        name: ROUTE_NAMES.GAME_PAGE,
        params: { game: props.unavailableGameReplace?.hru },
        query: { [SSR_QUERY.AUTOPLAY]: 'true' },
    })
}
</script>
<style module="$styleCrit" critical>
.container {
    --container-border-radius: 12px;
    --media-width: clamp(230px, 80%, 800%);

    background: #1d1c26;
    border-radius: var(--container-border-radius);
    display: flex;
    flex-direction: column;
    position: relative;
}

.content {
    position: relative;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 12px;
    flex-grow: 1;
    width: 100%;
    box-sizing: border-box;
    align-items: center;
    -webkit-touch-callout: none;
    -webkit-user-select: none;
    user-select: none;
    border-radius: var(--container-border-radius) var(--container-border-radius) 0 0;
    background-size: cover;
    background:
        radial-gradient(
            ellipse at 50% 20%,
            rgba(112, 94, 148, 0.7) 0%,
            rgba(40, 50, 40, 0.5) 50%,
            rgba(10, 15, 10, 0.85) 100%
        ),
        radial-gradient(ellipse at 30% 60%, rgba(202, 208, 202, 0.6) 0%, transparent 70%);
}

.short_game_info_override {
    align-self: flex-start;
}

.short_game_info_override,
.mobile_footer_override {
    visibility: hidden;
    height: 48px;
}

.play_button_override {
    display: none;
}

@media (--mobile) {
    .container {
        --container-border-radius: 0;
        margin: 0 calc(var(--global-padding-horizontal) * -1) 0;
    }

    .content.init_state {
        padding: 6px;
    }

    .content {
        height: 100%;
        gap: 12px;
        background: linear-gradient(
            0deg,
            var(--carbon-100) 0%,
            #2f2e4a 10%,
            #403f60 30%,
            #50516c 50%,
            #403f60 70%,
            #2f2e4a 90%,
            var(--carbon-100) 100%
        );
    }
}

@media (--mobile-landscape) {
    .content {
        display: grid;
        grid-template-columns: 1fr 1fr;
        grid-template-rows: 1fr auto 1fr;
        grid-template-areas:
            'preview short-info'
            'preview play-button'
            'preview footer';
        background: var(--carbon-100);
    }

    .unavailable_game_override {
        grid-area: preview;
    }

    .short_game_info_override {
        grid-area: short-info;
        align-self: end;
    }

    .play_button_override {
        display: block;
        grid-area: play-button;
    }

    .mobile_footer_override {
        grid-area: footer;
        align-self: start;
    }
}

@media (--tablet-plus) {
    .content {
        --media-width: clamp(230px, 50%, 50%);
    }

    .container {
        aspect-ratio: var(--thumbnail-aspect-ratio);
        height: auto;
    }

    .short_game_info_override {
        display: none;
    }
}

@media (--desktop-small-plus) {
    .container {
        height: 100%;
        width: 100%;
        min-height: auto;
        aspect-ratio: auto;
    }

    .init_state {
        padding-top: 52px;
    }
}

@media (--desktop-medium-plus) {
    .content {
        --media-width: clamp(230px, 40%, 40%);
    }
}

@media (--desktop-large-plus) {
    .content {
        --media-width: clamp(230px, 50%, 50%);
    }
}

@media (--desktop-xlarge-plus) {
    .content {
        --media-width: clamp(230px, 60%, 60%);
    }
}
</style>
<style module>
.short_game_info_override,
.mobile_footer_override {
    visibility: visible;
}
</style>
